import BackTo from '@components/back-to'
import Breadcrumbs from '@components/breadcrumbs'
import Layout from '@components/layout'
import { blogPostPageModuleQuery } from '@data/sanity/queries/page'
import { SanityBlogPostPage } from '@data/sanity/queries/types/blog'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getBlogCanonicalUrl } from '@lib/blog'
import { Locale, getAllLocales } from '@lib/language'
import {
  BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import {
  CustomStaticPropsContext,
  StaticPathWithSlug,
  getPageUrl,
} from '@lib/routes'
import {
  getAllBlogSettings,
  getBlogLocale,
  getBlogPostPage,
} from '@lib/sanity/blog'
import { getSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getAllSlugs } from '@lib/sanity/page'
import { getBlogPostSchema } from '@lib/schema/blog'
import { getBreadcrumbListSchema } from '@lib/schema/breadcrumb'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import { StringsContext } from '@lib/strings-context'
import Modules from '@modules/modules'
import { GetStaticPathsResult, GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { ParsedUrlQuery } from 'querystring'
import { useContext, useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type BlogPostPageProps = BasePageProps & {
  blogLocale: Locale
  slug: string
  page: SanityBlogPostPage
  site: SanitySiteFragment
}

const BlogPostPage = ({
  draftMode,
  locale,
  blogLocale,
  slug,
  page,
  site,
}: BlogPostPageProps) => {
  const strings = useContext(StringsContext)
  const canonicalUrl = getBlogCanonicalUrl('blogPost', slug, site.blogSettings)
  const blogPageUrl = getPageUrl(locale, 'blogPage')

  const schemas = useMemo(() => {
    const blogPostSchema = getBlogPostSchema(
      locale,
      page,
      site.generalSettings.siteURL,
    )
    const organizationUrl = site.schemaSettings?.organization?.url
    const blogPageUrl = getPageUrl(locale, 'blogPage')
    const blogPostUrl = getPageUrl(locale, 'blogPost', slug)
    const breadcrumbListSchema = getBreadcrumbListSchema([
      {
        title: page.blogPage?.seo?.metaTitle,
        url: `${organizationUrl}${blogPageUrl}`,
      },
      {
        title: page.seo?.metaTitle ?? page.title,
        url: `${organizationUrl}${blogPostUrl}`,
      },
    ])
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [blogPostSchema, breadcrumbListSchema, ...videoObjectSchemas]
  }, [locale, page, site, slug])

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      schemas={schemas}
      draftMode={draftMode}
      footer={<BackTo title={strings.blogTitle} url={blogPageUrl} />}
    >
      <Breadcrumbs
        items={[
          {
            url: blogPageUrl,
            title: strings.blogTitle,
          },
          {
            title: page.title,
          },
        ]}
      />

      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={blogPostPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            blogLocale,
            blogPostSlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

interface BlogPostPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<BlogPostPageParams>,
): Promise<GetStaticPropsResult<BlogPostPageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const blogLocale = await getBlogLocale(sanityClient, context.locale)
  const slug = context.params?.slug ?? ''
  const blogPostPage = await getBlogPostPage(
    sanityClient,
    context.locale,
    blogLocale,
    slug,
  )

  if (!blogPostPage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      blogLocale: blogLocale as Locale,
      slug,
      page: getNonNullValues(blogPostPage.page),
      site: getNonNullValues(blogPostPage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<BlogPostPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const blogPosts = await getAllSlugs(sanityClient, 'blogPost')
  const siteLocales = getAllLocales()
  const blogSettings = await getAllBlogSettings(sanityClient, siteLocales)
  const paths: StaticPathWithSlug[] = []

  blogPosts.forEach((blogPost) => {
    const locale = blogPost.locale as Locale

    if (blogSettings.hasLocaleContent[locale]) {
      // Add page locale path
      paths.push({
        params: {
          slug: blogPost.slug.current,
        },
        locale,
      })

      // Add inherited locale paths
      for (const childLocale of blogSettings.childLocales[locale]) {
        paths.push({
          params: {
            slug: blogPost.slug.current,
          },
          locale: childLocale,
        })
      }
    }
  })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default BlogPostPage
